import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../utils/url-manager';

/**
 * One thing needing attention. Deliberately shapeless: the bell renders a title, a subtitle and a
 * time, and navigates to whatever route the source gave it, without knowing what any of it is.
 *
 * That is what lets a new kind of notification be added in HQ-API alone — a class implementing
 * `NotificationProviderInterface` — with no change to this file or to the bell.
 */
export type NotificationItem = {
    id: string,
    title: string,
    subtitle: string,
    /** An HQ path, or null when the item has nowhere to go. */
    route: string|null,
    /** ISO 8601, or null. */
    at: string|null,
};

/** One source's worth, rendered as a labelled section of the dropdown. */
export type NotificationGroup = {
    key: string,
    label: string,
    count: number,
    items: NotificationItem[],
};

export type Notifications = {
    count: number,
    groups: NotificationGroup[],
};

export const EMPTY_NOTIFICATIONS: Notifications = { count: 0, groups: [] };

/**
 * Fired by anything that has just changed what the bell should say.
 *
 * The bell first re-polled on a timer after navigating, which was a guess at how long the target
 * screen would take to clear its own notifications — and it lost the race: the reply was marked
 * read server-side while the badge went on showing 1 until the next minute ticked. An event has no
 * timing to get wrong, and any future source can use the same one.
 */
export const NOTIFICATIONS_CHANGED: string = 'noknok:notifications-changed';

/** Tell the bell something changed. Safe to call when no bell is mounted. */
export const notificationsChanged = (): void => {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));
};

/**
 * The bell's poll.
 *
 * Its own module rather than a react-admin resource: this runs on every screen on a timer, so it
 * has to stay cheap and, above all, non-blocking. HQ was slowed once already by a check placed in
 * front of every navigation (see the `checkAuth` note in `authProvider`), so nothing here is
 * awaited by anything that renders a route.
 */
export const loadNotifications = async (): Promise<Notifications> => {
    const token: string = read_cookie('auth');
    if (typeof token !== 'string' || token.length === 0) {
        // Signed out. Asking would answer 401 on every tick of the timer.
        return EMPTY_NOTIFICATIONS;
    }

    const url: string = UseUrlManagerCleanUrl({ url: `${ hostUrl }hq/notifications` });
    const response: Response = await fetch(new Request(url, {
        method: 'GET',
        headers: new Headers({ 'Content-Type': 'application/json', 'token': token, 'hq': 'hq' }),
    }));
    if (!response.ok) {
        throw new Error('Could not load notifications');
    }

    const body: any = await response.json();

    return {
        count: typeof body?.count === 'number' ? body.count : 0,
        groups: Array.isArray(body?.groups) ? body.groups : [],
    };
};
